import type {
  InvoiceEmailSuggestion,
  InvoiceEmailSuggestionKind,
} from './types';

import {
  type DefaultError,
  queryOptions,
  type QueryFunctionContext,
  type UseQueryOptions,
  useQuery,
} from '@tanstack/react-query';
import { ms } from 'ms';

import type { UUID } from '~/types/common';

import { queryKeysInvoice } from '../queryKeys';

import { fetchInvoiceEmailSuggestion } from './fetchInvoiceEmailSuggestion';

/**
 * Generates query options for an invoice email suggestion.
 *
 * @param invoiceId - Invoice identifier.
 * @param kind - Email kind to suggest.
 * @param options - Additional query options.
 * @returns Query options for the email suggestion.
 */
// fallow-ignore-next-line unused-export -- canonical query-options entry point for email suggestion consumers
export function getInvoiceEmailSuggestionQueryOptions<
  TData = InvoiceEmailSuggestion,
>({
  invoiceId,
  kind,
  options,
}: {
  invoiceId: UUID;
  kind: InvoiceEmailSuggestionKind;
  options?: Omit<
    UseQueryOptions<InvoiceEmailSuggestion, DefaultError, TData>,
    'queryKey' | 'queryFn'
  >;
}) {
  return queryOptions({
    queryFn: (context: QueryFunctionContext) =>
      fetchInvoiceEmailSuggestion({ invoiceId, kind }, context),
    queryKey: queryKeysInvoice.getEmailSuggestion(invoiceId, kind),
    retry: 1,
    staleTime: ms('30s'),
    ...options,
  });
}

/**
 * Loads recipient, content, and attachable-file suggestions for an invoice
 * issuer email.
 *
 * @param invoiceId - Invoice identifier.
 * @param kind - Email kind to suggest.
 * @param options - Additional query options.
 * @returns The email suggestion query result.
 */
export function useQueryInvoiceEmailSuggestion<TData = InvoiceEmailSuggestion>(
  invoiceId: UUID,
  kind: InvoiceEmailSuggestionKind,
  options?: Omit<
    UseQueryOptions<InvoiceEmailSuggestion, DefaultError, TData>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery(
    getInvoiceEmailSuggestionQueryOptions({
      invoiceId,
      kind,
      options,
    }),
  );
}
